/**
 * Hook para gerenciamento da janela de 24h do WhatsApp Oficial e Instagram Direct
 * 
 * A Meta Cloud API (tanto WhatsApp quanto Instagram) exige que mensagens após 24h
 * da última resposta do contato só possam ser enviadas via templates aprovados (WABA)
 * ou ficam completamente bloqueadas (Instagram - não possui templates).
 * 
 * Este hook calcula o estado da janela e fornece temporizador regressivo.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Status da janela de 24 horas (WhatsApp Oficial e Instagram Direct)
 */
export interface SessionWindowStatus {
  /** Indica se a janela está expirada (>24h desde última msg inbound) */
  isExpired: boolean;
  /** Indica se é canal WhatsApp Oficial (aplica regra 24h) */
  isWhatsAppOficial: boolean;
  /** Indica se é canal Instagram Direct (aplica regra 24h sem templates) */
  isInstagramDirect: boolean;
  /** Indica se a regra de janela 24h se aplica ao canal (WABA ou Instagram) */
  appliesSessionWindow: boolean;
  /** Indica se o canal possui opção de enviar template para reabrir conversa */
  hasTemplateOption: boolean;
  /** Tempo restante formatado (HH:MM:SS) ou null se expirada */
  remainingTime: string | null;
  /** Tempo desde expiração formatado (ex: "2h 15min") ou null se ativa */
  expiredSince: string | null;
  /** Timestamp da última mensagem inbound */
  lastInboundAt: Date | null;
  /** Segundos restantes (para ordenação/lógica) */
  remainingSeconds: number;
}

/**
 * Formata segundos em HH:MM:SS
 */
const formatTimeRemaining = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  return [hours, minutes, seconds]
    .map(v => v.toString().padStart(2, '0'))
    .join(':');
};

/**
 * Formata tempo decorrido em formato legível (ex: "2h 15min")
 */
const formatTimeAgo = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }
  return `${minutes}min`;
};

/**
 * Hook para calcular estado da janela de 24h do WhatsApp Oficial e Instagram Direct
 * 
 * @param channelType - Tipo do canal (ex: 'whatsapp_oficial', 'instagram', 'whatsapp')
 * @param lastInboundAt - Timestamp da última mensagem inbound (ISO string)
 * @returns Status da janela com temporizador
 */
export function useSessionWindow(
  channelType: string | undefined,
  lastInboundAt: string | null | undefined
): SessionWindowStatus {
  // Estado para forçar re-render a cada segundo (temporizador)
  const [tick, setTick] = useState(0);

  // Verifica se é WhatsApp Oficial (aplica regra 24h com templates)
  const isWhatsAppOficial = channelType === 'whatsapp_oficial';

  // Verifica se é Instagram Direct (aplica regra 24h sem templates)
  const isInstagramDirect = channelType === 'instagram';

  // Verifica se a regra de janela 24h se aplica ao canal
  const appliesSessionWindow = isWhatsAppOficial || isInstagramDirect;

  // WhatsApp Oficial pode enviar templates para reabrir; Instagram não
  const hasTemplateOption = isWhatsAppOficial;

  // Parse da data de última mensagem inbound
  const lastInboundDate = useMemo(() => {
    if (!lastInboundAt) return null;
    const date = new Date(lastInboundAt);
    return isNaN(date.getTime()) ? null : date;
  }, [lastInboundAt]);

  // Calcula status da janela
  const calculateStatus = useCallback((): Omit<SessionWindowStatus, 'isWhatsAppOficial' | 'isInstagramDirect' | 'appliesSessionWindow' | 'hasTemplateOption' | 'lastInboundAt'> => {
    // Se não aplica regra de janela 24h, janela sempre ativa
    if (!appliesSessionWindow) {
      return {
        isExpired: false,
        remainingTime: null,
        expiredSince: null,
        remainingSeconds: Infinity,
      };
    }

    // Se nunca recebeu mensagem inbound, janela expirada (precisa de template ou aguardar contato)
    if (!lastInboundDate) {
      return {
        isExpired: true,
        remainingTime: null,
        expiredSince: 'Nunca respondeu',
        remainingSeconds: 0,
      };
    }

    const now = new Date();
    const windowEnd = new Date(lastInboundDate.getTime() + 24 * 60 * 60 * 1000); // +24h
    const diffMs = windowEnd.getTime() - now.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);

    if (diffSeconds <= 0) {
      // Janela expirada - calcula tempo desde expiração
      const expiredSeconds = Math.abs(diffSeconds);
      return {
        isExpired: true,
        remainingTime: null,
        expiredSince: formatTimeAgo(expiredSeconds),
        remainingSeconds: 0,
      };
    }

    // Janela ainda ativa
    return {
      isExpired: false,
      remainingTime: formatTimeRemaining(diffSeconds),
      expiredSince: null,
      remainingSeconds: diffSeconds,
    };
  }, [appliesSessionWindow, lastInboundDate]);

  // Efeito para atualizar temporizador a cada segundo (apenas se aplica janela 24h)
  useEffect(() => {
    if (!appliesSessionWindow) return;

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [appliesSessionWindow]);

  // Calcula status atual (recalculado a cada tick)
  const currentStatus = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = tick; // Força recálculo quando tick muda
    return calculateStatus();
  }, [calculateStatus, tick]);

  return {
    ...currentStatus,
    isWhatsAppOficial,
    isInstagramDirect,
    appliesSessionWindow,
    hasTemplateOption,
    lastInboundAt: lastInboundDate,
  };
}
